import { NgClass } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { ChangeAccountingPlanEndDateComponent } from 'components/accounting/accounting-plan/change-accounting-plan-end-date.component';
import { LedgerPostsComponent } from 'components/accounting/ledger/ledger-posts.component';
import { DateInputComponent } from 'components/input/date-input/date-input.component';
import { ReasonSelectComponent } from 'components/input/reason-select/reason-select.component';
import { TooltipComponent } from 'components/shared/tooltip/tooltip.component';
import { CredentialType } from 'enums/CredentialType';
import { LedgerPostType } from 'enums/LedgerPostType';
import { ReasonType } from 'enums/ReasonType';
import { EndAccountingPlanDtoModel } from 'models/generated/Accounting.Api.Models.CreditWizardRequest';
import { LedgerPostModel } from 'models/generated/Entities.Accounting.LedgerPost';
import { ReasonModel } from 'models/generated/Entities.GormCrm.Reason';
import { SelectListItemModel } from 'models/SelectList';
import { Inputs } from 'modules/inputs';
import { Layout } from 'modules/layout';
import { TranslatePipe } from 'pipes/translate.pipe';
import { AccountingService } from 'services/accounting.service';
import { CredentialRequired } from 'services/credential.service';
import { DateService } from 'services/date.service';
import { FormValidationService } from 'services/form-validation.service';
import { NavigationService } from 'services/navigation.service';
import { ProductPlanService } from 'services/productplan.service';

@CredentialRequired(CredentialType.EditAccountingPlan)
@Component({
	templateUrl: './terminate-product-plan.component.html',
	providers: [FormValidationService],
	imports: [Layout, Inputs, ReasonSelectComponent, TooltipComponent, NgClass, LedgerPostsComponent, TranslatePipe]
})
export class TerminateProductPlanComponent extends ChangeAccountingPlanEndDateComponent {

	@ViewChild('terminationDateInput') terminationDateInput?: DateInputComponent;

	private _nextPeriod?: number;
	reasonType = ReasonType;
	newTerminationDate?: Date;
	maxTerminationDate: Date;
	selectedTerminationReason: ReasonModel | undefined;
	allFutureInvoices?: LedgerPostModel[] | undefined;
	askQuestionRemoveAll = false;
	removeAllUnpostedInvoicePosts?: boolean | undefined;
	ledgerPostTypes = LedgerPostType;
	renewPlan?: boolean;
	periodOptions: SelectListItemModel[];

	public get nextPeriod() { return this._nextPeriod ?? this.productPlan?.nextPeriod; }
	public set nextPeriod(newPeriod: number | undefined) { this._nextPeriod = newPeriod ? Math.min(newPeriod, 24) : newPeriod; }

	private _requestRenewal: boolean = false;
	public get requestRenewal(): boolean {

		let requestRenewal = false;
		if (this.productPlan?.autoRenewalDate && this.newTerminationDate) {

			const compareDate = this.dateService.addMonths(this.productPlan.autoRenewalDate, -this.productPlan.terminationNoticePeriod);
			requestRenewal = this.newTerminationDate > compareDate;
		}
		if (requestRenewal != this._requestRenewal) {
			this._requestRenewal = requestRenewal;
			this.validateView();
		}
		return requestRenewal;
	}

	terminateProductPlan() {
		if (!this.newEndDate || !this.accountingPlan || !this.newTerminationDate || !this.nextPeriod) return;

		const request = new EndAccountingPlanDtoModel();

		request.accountingPlanId = this.accountingPlan.id;
		request.endDate = this.newEndDate;
		request.terminationDate = this.newTerminationDate;
		request.terminationReasonId = this.selectedTerminationReason?.id;
		request.removeAllUnpostedInvoicePosts = this.removeAllUnpostedInvoicePosts;
		if (this.renewPlan === true) {
			request.createRenewal = true;
			request.nextPeriod = this.nextPeriod;
		}

		this.accountingService.endAccountingPlan(request).then(() => { this.navigation.closeView(this); });
	}

	getEndDatesFilter() {
		return this.subscriptionEndDatesFilter ? this.subscriptionEndDatesFilter.concat([new Date()]) : undefined;
	}

	changedEndDate(newEndDate: Date) {
		if (this.ledgerPosts) {
			this.allFutureInvoices = this.ledgerPosts.allPosts.filter(post => post.plannedDate && !post.ledgerHeaderId && (post.type == this.ledgerPostTypes.Invoice || post.type == this.ledgerPostTypes.StartupInvoice));
			let postsAfterEndDate = this.allFutureInvoices.filter(post => this.dateService.getDate(post.plannedDate!) > this.dateService.getDate(newEndDate));
			this.askQuestionRemoveAll = postsAfterEndDate.length > 0 && this.allFutureInvoices.length > postsAfterEndDate.length;
			this.removeAllUnpostedInvoicePosts = this.askQuestionRemoveAll ? undefined : postsAfterEndDate.length > 0;
			this.updatePostsToRemove(postsAfterEndDate);
		} else {
			this.askQuestionRemoveAll = false;
			this.removeAllUnpostedInvoicePosts = false;
		}
		this.validateView();
	}

	removeAll(shouldRemoveAll: boolean) {
		this.removeAllUnpostedInvoicePosts = shouldRemoveAll;
		this.askQuestionRemoveAll = false;
		if (shouldRemoveAll && this.ledgerPosts && this.allFutureInvoices)
			this.updatePostsToRemove(this.allFutureInvoices);
		this.validateView();
	}

	updatePostsToRemove(postsToRemove: LedgerPostModel[]) {
		this.ledgerPosts!.disabledPosts = postsToRemove;
	}

	validateView() {
		let valid = true;
		if (this.askQuestionRemoveAll && this.removeAllUnpostedInvoicePosts === undefined) valid = false;
		if (this.requestRenewal && this.renewPlan === undefined) valid = false;
		this.formValidation.fieldStateChanged(this, valid, false);
	}

	markAsPristine() { }

	constructor(
		accountingService: AccountingService,
		productPlanService: ProductPlanService,
		navigation: NavigationService,
		private readonly formValidation: FormValidationService,
		dateService: DateService) {

		super(accountingService, productPlanService, navigation, dateService);

		this.periodOptions = [
			new SelectListItemModel(12, '12'),
			new SelectListItemModel(24, '24')
		];

		this.maxTerminationDate = dateService.today();
		setTimeout(() => {
			this.terminationDateInput!.valueChanged();
		});
	}
}
